import { ErrorModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	MenuItem,
	MenuList,
	Paper,
	Snackbar,
	Typography,
	SnackbarCloseReason,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { type Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { TemplateAttachmentsTable } from "./resources-tables/TemplateAttachments/TemplateAttachmentsTable";
import { Alert } from "@mui/lab";
import { AssetAttachmentPicker } from "./resources-tables/TemplateAttachments/components/asset-Attachments/asset-Attachments-picker";
import { useSearchTemplateAttachments } from "./resources-tables/TemplateAttachments/hooks/useSearchTemplateAttachments";
import { getCrafts } from "@/pages/task-list/repositorie/TaskListRepositorie";
import { get } from "lodash-es";
import { useTemplatesContext } from "../../context/templatesContext";
import { useSearchAssetResources } from "../hooks/useSearchAssetResources";
import { useSearchTemplateResources } from "../hooks/useSearchTemplateResources";
import type { Craft } from "@/shared/work-orders-modals/models/Craft";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		height: "100%",
	},
	resourcesList: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	menuList: {
		flexGrow: 1,
		overflowY: "auto",
		padding: 0,
	},
	menuItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		minWidth: "250px",
	},
	menuItemSelected: {
		backgroundColor: "rgba(173, 216, 230, 0.2)",
	},
	menuItemContent: {
		width: "100%",
		padding: theme.spacing(2),
	},
	tableContainer: {
		height: "100%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	// swv-cmms only uses Attachments
	attachmentsMenuItem: {
		backgroundColor: "rgba(255, 165, 0, 0.1)",
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	AssetId: number | null;
	TemplateId: number | null;
	newMode?: boolean;
	Title: string;
	AssetName: string;
	TypeCode: string;
	CraftId?: number;
};

// swv-cmms only uses Attachments (not Drawings, LOTO, Parts like roanoke-cmms)
export const TableTemplateResoures = ({
	show,
	onHide,
	AssetId,
	TemplateId,
	newMode,
	Title,
	AssetName,
	TypeCode,
	CraftId,
}: Props) => {
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("xl");
	const classes = useStyles();

	const {
		state: {
			assetAttachments,
			templateAttachmentsList,
		},
		actions: {
			setAssetAttachmentsList,
			setTemplateAttachmentsList,
		},
	} = useTemplatesContext();

	const [isTemplateAttachmentsLoading, setIsTemplateAttachmentsLoading] =
		useState<boolean>(false);

	const [templateAttachmentsError, setTemplateAttachmentsError] =
		useState<string>("");

	const [isLoadingResourcesList, setIsLoadingResourcesList] =
		useState<boolean>(false);
	const [errorResourcesList, setErrorResourcesList] = useState<string>("");
	const [assetResourcesMessage, setAssetResourcesMessage] = useState(false);

	const [showAssetAttachmentsPicker, setShowAssetAttachmentsPicker] =
		useState(false);

	const [isLoadingTemplateResourcesList, setIsLoadingTemplateResourcesList] =
		useState<boolean>(false);

	const [errorTemplateResourcesList, setErrorTemplateResourcesList] =
		useState<string>("");

	const [craftName, setCraftName] = useState<string>("");

	// Always show attachments (only resource type in swv-cmms)
	const [selectedItem] = useState<string>("ATTACHMENTS");

	const searchTemplateAttachments = useSearchTemplateAttachments({
		TemplateId,
		setIsTemplateAttachmentsLoading,
		setTemplateAttachmentsError,
	});

	const searchAssetResources = useSearchAssetResources({
		AssetId,
		setAssetAttachmentsList,
		setErrorResourcesList: setErrorResourcesList,
		setIsLoadingResourcesList,
		setAssetResourcesMessage,
	});

	const searchTemplateResources = useSearchTemplateResources({
		TemplateId,
		setTemplateAttachmentsList,
		setErrorTemplateResourcesList: setErrorTemplateResourcesList,
		setIsLoadingTemplateResourcesList,
	});

	const handleCloseResourcesMessage = (
		event: Event | React.SyntheticEvent,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetResourcesMessage(false);
	};

	const handleCancel = () => {
		onHide(false);
		setTemplateAttachmentsList([]);
	};

	const getCraftName = async (craftId: number) => {
		try {
			const res = await getCrafts();
			if (res.ok) {
				const craftsData = get(res, "data.tables[0].rows", []);
				const craft = craftsData.find((c: Craft) => c.CraftId === craftId);
				if (craft) {
					setCraftName(craft.CraftName);
				}
			}
		} catch (error) {
			console.error("Error fetching craft name:", error);
		}
	};

	useEffect(() => {
		if (show) {
			searchAssetResources();
			searchTemplateResources();
		} else {
			handleCancel();
		}
	}, [show]);

	useEffect(() => {
		if (CraftId) {
			getCraftName(CraftId);
		}
	}, [CraftId]);

	return (
		<>
			<Dialog
				maxWidth={maxWidth}
				open={show}
				onClose={handleCancel}
				fullWidth={true}
				style={{ margin: "0", padding: 5, width: "100% !important" }}
				className={classes.root}
			>
				<Grid2
					container
					justifyContent="center"
					style={{
						height: "100vh",
						paddingBottom: 0,
						display: "flex",
						alignItems: "center",
					}}
				>
					<Grid2
						style={{
							padding: "0 30px",
							height: "auto",
							minHeight: "8vh",
						}}
						size={{ md: 12, xs: 12 }}
					>
						<Typography
							variant="h5"
							style={{
								fontWeight: 600,
								paddingTop: 10,
							}}
						>
							TEMPLATE RESOURCES
						</Typography>

						<Typography
							style={{
								display: "flex",
								alignItems: "center",
								fontSize: "1.3rem",
								flexWrap: "wrap",
								gap: "8px",
								padding: "8px 0 0",
							}}
						>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Template:</span>
								<span style={{ marginLeft: "4px" }}>{Title}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Asset:</span>
								<span style={{ marginLeft: "4px" }}>{AssetName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Type:</span>
								<span style={{ marginLeft: "4px" }}>{TypeCode}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Craft:</span>
								<span style={{ marginLeft: "4px" }}>{craftName}</span>
							</div>
						</Typography>
					</Grid2>
					<DialogContent
						dividers
						style={{ height: "66vh", paddingTop: "20px", overflowY: "hidden" }}
					>
						<Grid2
							container
							justifyContent="space-between"
							alignItems="stretch"
							className={classes.root}
							style={{ height: "100%" }}
						>
							{/* Left sidebar menu - swv-cmms only has ATTACHMENTS */}
							<Grid2 style={{ height: "100%" }} size={{ md: 3, xs: 12 }}>
								<Paper elevation={1} className={classes.resourcesList}>
									<MenuList className={`mes-submenu ${classes.menuList}`}>
										<MenuItem
											className={`${classes.menuItem} ${classes.attachmentsMenuItem} ${classes.menuItemSelected}`}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													ATTACHMENTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														templateAttachmentsList.length > 0 &&
														` (${templateAttachmentsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
									</MenuList>
								</Paper>
							</Grid2>
							{/* Right side - Attachments table */}
							<Grid2 style={{ height: "100%" }} size={{ md: 9, xs: 12 }}>
								<Paper elevation={1} className={classes.tableContainer}>
									<TemplateAttachmentsTable
										AssetId={AssetId}
										newMode={newMode}
										TemplateId={TemplateId}
										searchAssetResources={searchAssetResources}
										show={true}
										onHide={() => {}}
										setShowAssetAttachmentsPicker={
											setShowAssetAttachmentsPicker
										}
										isLoadingResourcesList={isLoadingResourcesList}
										TemplateName={Title}
										AssetName={AssetName}
									/>
								</Paper>
							</Grid2>
						</Grid2>
					</DialogContent>

					<Grid2 style={{ height: "70px" }} size={{ md: 12, xs: 12 }}>
						<DialogActions
							style={{
								padding: "0px 24px",
								height: "100%",
								display: "flex",
								alignItems: "center",
							}}
						>
							<Grid2
								container
								spacing={2}
								style={{ margin: 0, display: "flex", alignItems: "center" }}
							>
								<Grid2 size={{ md: 3, xs: 4 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										Close
									</Button>
								</Grid2>
							</Grid2>
							<Grid2  size={{ md: 4, xs: 12 }}>
								<Button
									variant="contained"
									color="primary"
									onClick={() => setShowAssetAttachmentsPicker(true)}
									fullWidth
									disabled={assetAttachments.length === 0}
									style={{ minWidth: 300, height: 40 }}
								>
									ATTACHMENTS from asset
								</Button>
							</Grid2>
						</DialogActions>
						<AssetAttachmentPicker
							show={showAssetAttachmentsPicker}
							TemplateId={TemplateId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchTemplateAttachments();
								}
								setShowAssetAttachmentsPicker(false);
							}}
							TemplateName={Title}
							AssetName={AssetName}
							Title={Title}
						/>
					</Grid2>
					<ErrorModal
						error={errorResourcesList}
						onHide={() => setErrorResourcesList("")}
					/>
					<ErrorModal
						error={templateAttachmentsError}
						onHide={() => setTemplateAttachmentsError("")}
					/>
					<Snackbar
						open={assetResourcesMessage}
						autoHideDuration={2500}
						onClose={handleCloseResourcesMessage}
						anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					>
						<Alert severity="warning" onClose={handleCloseResourcesMessage}>
							The asset has no assigned resources
						</Alert>
					</Snackbar>
				</Grid2>
			</Dialog>
		</>
	);
};
